import React, { useState, useEffect } from 'react';
import { FiX, FiCalendar, FiClock, FiChevronDown, FiMapPin } from 'react-icons/fi';
import { createFtlLoad, postLoad } from '../../ApiController/ApiService';
import LocationPickerModal from './LocationPickerModal';
import { toast } from 'react-toastify';

export default function FTLModal({ onClose, openImport, openExport, openLTL }) {
  const [formData, setFormData] = useState({
    pickupAddress: '',
    deliveryAddress: '',
    pickupDate: '',
    pickupTime: '',
    deliveryDeadline: '',
    truckSize: '53 ft',
    commodity: '',
    pallets: '',
    pieces: '',
    weight: '',
    deliveryPrice: '',
    negotiable: false,
    shippingItemType: 'Other',
    boxes: '',
    length: '',
    width: '',
    height: '',
    weightPerBox: '',
    liftGateRequired: false,
    loadCategory: '',
    specialInstructions: '',
    pickupLat: null,
    pickupLng: null,
    deliveryLat: null,
    deliveryLng: null
  });

  const [showMap, setShowMap] = useState(false);
  const [mapType, setMapType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpenMap = (type) => {
    setMapType(type);
    setShowMap(true);
  };

  const handleMapSave = ({ lat, lng, address }) => {
    if (mapType === 'pickup') {
      setFormData(prev => ({ ...prev, pickupAddress: address, pickupLat: lat, pickupLng: lng }));
    } else {
      setFormData(prev => ({ ...prev, deliveryAddress: address, deliveryLat: lat, deliveryLng: lng }));
    }
  };

  const [isSearchingPickup, setIsSearchingPickup] = useState(false);
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [isSearchingDelivery, setIsSearchingDelivery] = useState(false);
  const [deliverySuggestions, setDeliverySuggestions] = useState([]);

  // Autocomplete for Pickup
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (formData.pickupAddress && formData.pickupAddress.trim().length > 2 && !formData.pickupLat) {
        setIsSearchingPickup(true);
        try {
          const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.pickupAddress)}&limit=5`);
          const data = await response.json();
          setPickupSuggestions(data || []);
        } catch (error) {
          setPickupSuggestions([]);
        } finally {
          setIsSearchingPickup(false);
        }
      } else {
        setPickupSuggestions([]);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [formData.pickupAddress, formData.pickupLat]);

  // Autocomplete for Delivery
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (formData.deliveryAddress && formData.deliveryAddress.trim().length > 2 && !formData.deliveryLat) {
        setIsSearchingDelivery(true);
        try {
          const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.deliveryAddress)}&limit=5`);
          const data = await response.json();
          setDeliverySuggestions(data || []);
        } catch (error) {
          setDeliverySuggestions([]);
        } finally {
          setIsSearchingDelivery(false);
        }
      } else {
        setDeliverySuggestions([]);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [formData.deliveryAddress, formData.deliveryLat]);

  const handleSelectPickup = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      pickupAddress: suggestion.display_name,
      pickupLat: parseFloat(suggestion.lat),
      pickupLng: parseFloat(suggestion.lon)
    }));
    setPickupSuggestions([]);
  };

  const handleSelectDelivery = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      deliveryAddress: suggestion.display_name,
      deliveryLat: parseFloat(suggestion.lat),
      deliveryLng: parseFloat(suggestion.lon)
    }));
    setDeliverySuggestions([]);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'pickupAddress') {
        next.pickupLat = null;
        next.pickupLng = null;
      }
      if (name === 'deliveryAddress') {
        next.deliveryLat = null;
        next.deliveryLng = null;
      }
      return next;
    });
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (formData.deliveryDeadline && formData.pickupDate) {
      if (new Date(formData.deliveryDeadline) < new Date(formData.pickupDate)) {
        toast.error("Delivery Deadline cannot be before Pickup Date", { position: "top-center" });
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const payload = {
        loadType: 'FTL',
        truckSize: formData.truckSize || '53 ft',
        origin: formData.pickupAddress,
        originLat: formData.pickupLat,
        originLng: formData.pickupLng,
        destination: formData.deliveryAddress,
        destinationLat: formData.deliveryLat,
        destinationLng: formData.deliveryLng,
        pickupDate: formData.pickupDate,
        ...(formData.deliveryDeadline && { deliveryDate: formData.deliveryDeadline }),
        shippingItemType: formData.shippingItemType || 'Other',
        needsLiftgate: Boolean(formData.liftGateRequired),
        commodity: formData.commodity,
        weight: Number(formData.weight) || 1, // Fallback to 1 to avoid 'weight is required' if 0
        ...(Number(formData.pallets) > 0 && { pallets: Number(formData.pallets) }),
        ...(Number(formData.pieces) > 0 && { pieces: Number(formData.pieces) }),
        ...(Number(formData.boxes) > 0 && { boxes: Number(formData.boxes) }),
        ...(Number(formData.length) > 0 && { length: Number(formData.length) }),
        ...(Number(formData.width) > 0 && { width: Number(formData.width) }),
        ...(Number(formData.height) > 0 && { height: Number(formData.height) }),
        deliveryPrice: Number(formData.deliveryPrice) || 0,
        negotiable: Boolean(formData.negotiable),
        additionalDetails: {
          loadCategory: formData.loadCategory || 'Other',
          ...(formData.specialInstructions && { specialInstructions: formData.specialInstructions })
        }
      };

      if (formData.shippingItemType === 'Box/Package') {
        const boxesCount = Number(formData.boxes);
        const wPerBox = Number(formData.weightPerBox);
        const totalW = boxesCount * wPerBox;
        
        delete payload.weight;
        
        payload.packageDetails = {
          numberOfBoxes: boxesCount,
          weightPerBox: wPerBox,
          totalWeight: totalW,
          dimensions: {
            length: Number(formData.length) || 1,
            width: Number(formData.width) || 1,
            height: Number(formData.height) || 1,
            unit: 'in'
          }
        };
      }

      const response = await postLoad(payload);
      console.log('FTL Load Created via postLoad:', response);
      toast.success(`Load Created Successfully. Drivers notified: ${response?.notification?.notifiedCount || 0}`, { position: "top-center" });
      if (onClose) onClose();
    } catch (error) {
      console.error(error);
      if (error?.errors && Array.isArray(error.errors)) {
        toast.error(error.errors.join('\n'), { position: "top-center" });
      } else if (error?.response?.data?.errors) {
        toast.error(error.response.data.errors.join('\n'), { position: "top-center" });
      } else {
        toast.error(error.message || "Failed to create FTL load", { position: "top-center" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-3 py-2.5 rounded bg-[#f1f5f9] text-gray-800 text-sm outline-none placeholder-gray-400 focus:ring-2 focus:ring-[#7c3aed]";
  const labelClass = "text-sm block mb-1 text-gray-200";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 font-sans">
      <div
        className="rounded-xl shadow-2xl text-white p-6 relative w-full max-w-[800px] max-h-[90vh] overflow-y-auto custom-scrollbar flex flex-col"
        style={{ background: 'radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-2xl text-gray-400 hover:text-white transition z-10"
        >
          <FiX />
        </button>

        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
          <span className="text-gray-400 font-light">+</span> Create New Container (FTL)
        </h2>

        {/* Tabs */}
        <div className="flex justify-center mb-6">
          <div className="bg-[#151b40] p-1 border border-[#2A2A4D] rounded-full flex w-full max-w-[500px] overflow-x-auto custom-scrollbar">
            <button
              type="button"
              onClick={openImport}
              className="flex-1 py-1.5 px-3 rounded-full text-sm font-semibold transition whitespace-nowrap bg-transparent text-[#6E7A90] hover:text-white"
            >
              Import
            </button>
            <button
              type="button"
              onClick={openExport}
              className="flex-1 py-1.5 px-3 rounded-full text-sm font-semibold transition whitespace-nowrap bg-transparent text-[#6E7A90] hover:text-white"
            >
              Export
            </button>
            <button
              type="button"
              className="flex-1 py-1.5 px-3 rounded-full text-sm font-semibold transition whitespace-nowrap bg-white text-[#003057]"
            >
              FTL
            </button>
            <button
              type="button"
              onClick={openLTL}
              className="flex-1 py-1.5 px-3 rounded-full text-sm font-semibold transition whitespace-nowrap bg-transparent text-[#6E7A90] hover:text-white"
            >
              LTL
            </button>
          </div>
        </div>

        <form onSubmit={handleAssign} className="space-y-6">
          {/* Shipment Details */}
          <div>
            <h3 className="text-cyan-400 font-semibold mb-3 border-b border-cyan-900 pb-1">Shipment Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelClass}>Pickup Address (Origin)*</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input name="pickupAddress" className={inputClass} value={formData.pickupAddress} onChange={handleInputChange} placeholder="Enter or select on map" required autoComplete="off" />
                    {pickupSuggestions.length > 0 && (
                      <ul className="absolute top-full left-0 right-0 bg-white text-black text-sm rounded shadow-lg mt-1 z-50 max-h-48 overflow-auto">
                        {pickupSuggestions.map((s, i) => (
                          <li key={i} className="px-3 py-2 border-b border-gray-100 last:border-0 hover:bg-gray-100 cursor-pointer" onClick={() => handleSelectPickup(s)}>
                            {s.display_name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button type="button" onClick={() => handleOpenMap('pickup')} className="bg-[#7c3aed] text-white px-3 rounded hover:bg-[#6d28d9] flex items-center justify-center min-w-[44px]">
                    <FiMapPin />
                  </button>
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Delivery Address*</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input name="deliveryAddress" className={inputClass} value={formData.deliveryAddress} onChange={handleInputChange} placeholder="Enter or select on map" required autoComplete="off" />
                    {deliverySuggestions.length > 0 && (
                      <ul className="absolute top-full left-0 right-0 bg-white text-black text-sm rounded shadow-lg mt-1 z-50 max-h-48 overflow-auto">
                        {deliverySuggestions.map((s, i) => (
                          <li key={i} className="px-3 py-2 border-b border-gray-100 last:border-0 hover:bg-gray-100 cursor-pointer" onClick={() => handleSelectDelivery(s)}>
                            {s.display_name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button type="button" onClick={() => handleOpenMap('delivery')} className="bg-[#7c3aed] text-white px-3 rounded hover:bg-[#6d28d9] flex items-center justify-center min-w-[44px]">
                    <FiMapPin />
                  </button>
                </div>
              </div>

              <div>
                <label className={labelClass}>Pickup Date</label>
                <div className="relative">
                  <input name="pickupDate" type="date" className={`${inputClass} appearance-none`} value={formData.pickupDate} onChange={handleInputChange} />
                  <FiCalendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className={labelClass}>Pickup Time</label>
                <div className="relative">
                  <input name="pickupTime" type="time" className={`${inputClass} appearance-none`} value={formData.pickupTime} onChange={handleInputChange} />
                  <FiClock className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className={labelClass}>Delivery Deadline</label>
                <div className="relative">
                  <input name="deliveryDeadline" type="date" className={`${inputClass} appearance-none`} value={formData.deliveryDeadline} onChange={handleInputChange} />
                  <FiCalendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          {/* Freight Details */}
          <div>
            <h3 className="text-cyan-400 font-semibold mb-3 border-b border-cyan-900 pb-1">Freight Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className={labelClass}>Commodity</label>
                <input name="commodity" className={inputClass} value={formData.commodity} onChange={handleInputChange} placeholder="Enter..." />
              </div>

              <div>
                <label className={labelClass}>Shipping Item Type</label>
                <div className="relative">
                  <select name="shippingItemType" className={`${inputClass} appearance-none`} value={formData.shippingItemType} onChange={handleInputChange}>
                    <option value="" disabled>Select one...</option>
                    <option value="Box/Package">Box/Package</option>
                    <option value="Pallet">Pallet</option>
                    <option value="Other">Other</option>
                  </select>
                  <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className={labelClass}>Truck Size</label>
                <div className="relative">
                  <input 
                    name="truckSize" 
                    list="truckSizeOptions"
                    className={inputClass} 
                    value={formData.truckSize} 
                    onChange={handleInputChange} 
                    placeholder="Select or type custom..."
                  />
                  <datalist id="truckSizeOptions">
                    <option value="53 ft" />
                    <option value="48 ft" />
                    <option value="Straight Truck" />
                  </datalist>
                </div>
              </div>

              <div>
                <label className={labelClass}>Total Weight (lbs)</label>
                <input name="weight" type="number" className={inputClass} value={formData.weight} onChange={handleInputChange} placeholder="0" />
              </div>
            </div>
          </div>

          {/* Package / Unit Details */}
          {formData.shippingItemType === 'Box/Package' ? (
            <div className="mt-4">
              <h3 className="text-cyan-400 font-semibold mb-3 border-b border-cyan-900 pb-1">Package Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <div className="grid grid-cols-2 gap-4 items-end">
                  <div>
                    <label className={labelClass}>Number of Boxes</label>
                    <input name="boxes" type="number" className={inputClass} value={formData.boxes} onChange={handleInputChange} placeholder="0" />
                  </div>
                  <div>
                    <label className={labelClass}>Weight Per Box (lbs)</label>
                    <input name="weightPerBox" type="number" className={inputClass} value={formData.weightPerBox} onChange={handleInputChange} placeholder="0" />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Dimensions (L x W x H in)</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input name="length" type="number" className={inputClass} value={formData.length} onChange={handleInputChange} placeholder="L" />
                    <input name="width" type="number" className={inputClass} value={formData.width} onChange={handleInputChange} placeholder="W" />
                    <input name="height" type="number" className={inputClass} value={formData.height} onChange={handleInputChange} placeholder="H" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <h3 className="text-cyan-400 font-semibold mb-3 border-b border-cyan-900 pb-1">Unit Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Pallets</label>
                  <input name="pallets" type="number" className={inputClass} value={formData.pallets} onChange={handleInputChange} placeholder="0" />
                </div>
                <div>
                  <label className={labelClass}>Total Pieces</label>
                  <input name="pieces" type="number" className={inputClass} value={formData.pieces} onChange={handleInputChange} placeholder="0" />
                </div>
              </div>
            </div>
          )}

          {/* Pricing Details */}
          <div>
            <h3 className="text-cyan-400 font-semibold mb-3 border-b border-cyan-900 pb-1">Pricing Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Delivery Price ($)</label>
                <input name="deliveryPrice" type="number" className={inputClass} value={formData.deliveryPrice} onChange={handleInputChange} placeholder="0.00" />
              </div>
              <div className="flex items-center mt-6">
                <input 
                  name="negotiable" 
                  type="checkbox" 
                  className="w-4 h-4 text-[#7c3aed] bg-gray-100 border-gray-300 rounded focus:ring-[#7c3aed]"
                  checked={formData.negotiable}
                  onChange={handleInputChange}
                />
                <label className="ml-2 text-sm text-gray-200">Price is Negotiable</label>
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div>
            <h3 className="text-cyan-400 font-semibold mb-3 border-b border-cyan-900 pb-1">Additional Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div className="w-full">
                <label className={labelClass}>Load Category</label>
                <div className="relative">
                  <select name="loadCategory" className={`${inputClass} appearance-none`} value={formData.loadCategory} onChange={handleInputChange}>
                    <option value="" disabled>Select Category...</option>
                    <option value="Distributor">Distributor</option>
                    <option value="Retailer">Retailer</option>
                    <option value="Warehouse">Warehouse</option>
                    <option value="Logistics">Logistics</option>
                    <option value="Broker">Broker</option>
                    <option value="Other">Other</option>
                  </select>
                  <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                </div>
              </div>
              
              <div className="w-full">
                <label className={labelClass}>Special Instructions</label>
                <input 
                  type="text"
                  name="specialInstructions" 
                  className={inputClass} 
                  value={formData.specialInstructions} 
                  onChange={handleInputChange} 
                  placeholder="e.g., Fragile items..." 
                />
              </div>

              <div className="w-full flex items-center h-[42px] px-2">
                <input 
                  name="liftGateRequired" 
                  type="checkbox" 
                  className="w-4 h-4 text-[#7c3aed] bg-gray-100 border-gray-300 rounded focus:ring-[#7c3aed]"
                  checked={formData.liftGateRequired}
                  onChange={handleInputChange}
                />
                <label className="ml-2 text-sm text-gray-200">Needs Liftgate</label>
              </div>
            </div>
          </div>

          {/* Billing & Contact Details Removed as per request */}

          <div className="sticky bottom-0 bg-[#0c102a] pt-4 pb-2 mt-4 z-10 border-t border-cyan-900">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 rounded-lg font-bold shadow-lg transition-all ${
                isSubmitting ? 'bg-gray-500 text-gray-300 cursor-not-allowed' : 'bg-[#7c3aed] hover:bg-[#6d28d9] text-white'
              }`}
            >
              {isSubmitting ? 'Assigning...' : 'Assign (Submit FTL)'}
            </button>
          </div>
        </form>
      </div>

      <LocationPickerModal 
        isOpen={showMap} 
        onClose={() => setShowMap(false)} 
        onSave={handleMapSave}
        title={mapType === 'pickup' ? 'Select Pickup Origin' : 'Select Delivery Destination'}
      />
    </div>
  );
}
